import React, { useState, useEffect } from 'react';
import { firebaseBrokerService } from '../services/FirebaseBrokerService';
import { Broker } from '../services/BrokerService';
import './IBPartners.css';

const IBPartners: React.FC = () => {
  const [brokers, setBrokers] = useState<Broker[]>([]);
  const [loading, setLoading] = useState(true);

  const loadBrokers = async () => {
    try {
      console.log('Loading brokers from Firebase...');
      setLoading(true);
      
      // Try to get all brokers first, then filter active ones
      const allBrokers = await firebaseBrokerService.getAllBrokers();
      console.log('All brokers from Firebase:', allBrokers);
      
      // Filter only active brokers
      const activeBrokers = allBrokers.filter(broker => broker.isActive === true);
      console.log('Active brokers:', activeBrokers);
      
      setBrokers(activeBrokers);
      
      // If no brokers found, log more info
      if (activeBrokers.length === 0) {
        console.log('No active brokers found. All brokers count:', allBrokers.length);
        console.log('First broker (if any):', allBrokers[0]);
      }
      
    } catch (error) {
      console.error('Failed to load brokers:', error);
      console.error('Error details:', error);
      setBrokers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBrokers();
  }, []);

  if (loading) {
    return (
      <div className="ib-partners-page">
        <div className="container">
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading brokers...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ib-partners-page">
      <div className="container">
        <div className="page-title">
          <h1>IB Partners</h1>
          <p>Partner with regulated and trusted brokers worldwide</p>
          
          {/* Debug button - remove in production */}
          <button 
            onClick={loadBrokers}
            style={{
              background: '#007bff',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '4px',
              cursor: 'pointer',
              marginTop: '10px'
            }}
          >
            🔄 Reload Brokers
          </button>
          
          {/* Debug info */}
          <p style={{ fontSize: '12px', color: '#888', marginTop: '5px' }}>
            Found {brokers.length} active brokers
          </p>
        </div>
        
        <div className="brokers-grid">
          {brokers.length > 0 ? (
            brokers.map((broker) => (
              <div key={broker.id} className="broker-card">
                <div className="broker-header">
                  {broker.logo && (
                    <img src={broker.logo} alt={broker.name} className="broker-logo" />
                  )}
                  <h3>{broker.name}</h3>
                  <div className="broker-rating">
                    <span className="rating-stars">
                      {Array.from({ length: 5 }, (_, i) => (
                        <span key={i} className={i < Math.floor(broker.rating) ? "star filled" : "star"}>
                          ⭐
                        </span>
                      ))}
                    </span>
                    <span className="rating-value">({broker.rating})</span>
                  </div>
                </div>
                
                <p className="broker-description">{broker.description}</p>
                
                <div className="broker-details">
                  <div className="detail-item">
                    <span className="label">Min Deposit:</span>
                    <span className="value">{broker.minDeposit}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Spreads:</span>
                    <span className="value">{broker.spreads}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Leverage:</span>
                    <span className="value">{broker.leverage}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Platforms:</span>
                    <span className="value">{broker.tradingPlatforms.join(', ')}</span>
                  </div>
                </div>
                
                <div className="broker-regulation">
                  <strong>Regulation:</strong> {broker.regulation.join(', ')}
                </div>
                
                <div className="broker-features">
                  {broker.features.map((feature, index) => (
                    <span key={index} className="feature-tag">{feature}</span>
                  ))}
                </div>
                
                <div className="broker-actions">
                  <a 
                    href={broker.referralLink || broker.website} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="btn primary"
                  >
                    Start Trading
                  </a>
                  <a 
                    href={broker.website} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="btn secondary"
                  >
                    Learn More
                  </a>
                </div>
              </div>
            ))
          ) : (
            <div className="no-brokers">
              <h3>No Brokers Available</h3>
              <p>We're currently updating our broker partnerships. Please check back soon.</p>
            </div>
          )}
        </div>
        
        <div className="partners-info">
          <h2>Why Choose UPM Traders?</h2>
          <div className="partners-features">
            <div className="feature">
              <h3>Regulated Brokers</h3>
              <p>We only list brokers regulated by top-tier financial authorities</p>
            </div>
            <div className="feature">
              <h3>Instant Withdrawals</h3>
              <p>Access your funds quickly with our verified instant withdrawal brokers</p>
            </div>
            <div className="feature">
              <h3>Crypto Trading</h3>
              <p>Specialized in cryptocurrency trading platforms with advanced tools</p>
            </div>
          </div>
        </div>
        
        <div className="partners-info">
          <h2>Why Partner With Regulated Brokers?</h2>
          <p>At UPM Traders, we partner exclusively with brokers that are regulated by top-tier financial authorities. This ensures that our clients' funds are protected, trading conditions are fair, and regulatory compliance is maintained at the highest level.</p>
          
          <div className="partners-features">
            <div className="feature">
              <h3>Fund Protection</h3>
              <p>Regulated brokers are required to keep client funds in segregated accounts</p>
            </div>
            <div className="feature">
              <h3>Transparent Trading</h3>
              <p>Regulated brokers must adhere to strict transparency requirements</p>
            </div>
            <div className="feature">
              <h3>Dispute Resolution</h3>
              <p>Access to compensation schemes in case of broker default</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IBPartners;